import type { TemplateOnlyComponent as TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import {
  PrismCard,
  PrismComboSelectInput,
  PrismInput,
  PrismMessage,
  PrismSelectInput,
  PrismTable,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import SettingsActivityModalMappingChanges from 'lumen/components/settings/activity/modal-mapping-changes';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type HotelSettingsActivityController from 'lumen/controllers/hotel/settings/activity';

import styles from './activity.module.css';

interface HotelSettingsActivitySignature {
  Args: {
    controller: HotelSettingsActivityController;
  };
}

const HotelSettingsActivity: TOC<HotelSettingsActivitySignature> = <template>
  <SettingsPageTemplate
    @description={{t "settings.activity_history.description"}}
    @hasAccountSelector={{true}}
    @isFullHeight={{true}}
    @title={{t "settings.activity_history.title"}}
  >
    <:link>
      <a
        class="prism-link prism-link--secondary prism-link--small"
        href="https://db-ip.com/"
        rel="noopener noreferrer"
        target="_blank"
      >{{t "settings.activity_history.ip_geolocation_attribution"}}</a>
    </:link>
    <:default>
      <div class="flex h-full flex-col">
        <PrismCard @contentLayout="noPadding" class="min-h-0 flex-1">
          <:content>
            <div class="flex h-full flex-col">
              <div
                class="flex shrink-0 items-center justify-end gap-200 px-400"
              >
                <div class={{styles.search-input}}>
                  <PrismInput
                    @iconAfter={{hash name="search"}}
                    @onInput={{@controller.updateSearch}}
                    @placeholder={{t
                      "settings.activity_history.filter.search_placeholder"
                    }}
                    @removePadding={{true}}
                    @value={{@controller.searchInputValue}}
                    data-test-id="search-input"
                  />
                </div>
                <div class={{styles.datepicker-wrapper}}>
                  <GeneralDatepickersDatepickerRange
                    @maxDate={{@controller.today}}
                    {{! @glint-expect-error: Incorrect type }}
                    @range={{@controller.dateRange}}
                    @rangeChanged={{@controller.updateDateRange}}
                  />
                </div>
                <PrismComboSelectInput
                  @buildLabelWithCount={{@controller.usersLabelWithCount}}
                  @isSearchable={{true}}
                  @onChange={{@controller.updateSelectedUsers}}
                  @onClose={{@controller.resetUserSearch}}
                  @onSearch={{@controller.searchUsers}}
                  @options={{@controller.filteredUserOptions}}
                  @selectAllLabel={{t
                    "settings.activity_history.filter.all_users"
                  }}
                  @value={{@controller.selectedUsers}}
                />
                <PrismSelectInput
                  @onChange={{@controller.updateSelectedCategory}}
                  @options={{@controller.categoryOptions}}
                  @placeholder={{t "events.filter.all_categories"}}
                  @value={{@controller.selectedCategory}}
                />
                <PrismSelectInput
                  @onChange={{@controller.updateSelectedActionStatus}}
                  @options={{@controller.actionStatusOptions}}
                  @value={{@controller.selectedActionStatus}}
                />
              </div>
              <div class="h-full min-h-0">
                <PrismTable
                  @columns={{@controller.columns}}
                  @enableAutoSortsUpdate={{true}}
                  @initialSorts={{@controller.initialSorts}}
                  @isLoading={{@controller.isActivityLogsRunning}}
                  @onRowCollapseToggle={{@controller.onRowCollapseToggle}}
                  @rows={{@controller.formattedActivityLogs}}
                  @widthConstraint="gte-container-slack"
                >
                  <:emptyState>
                    <div class="flex h-full w-full items-center justify-center">
                      <PrismMessage
                        @illustration="100x100_search"
                        @title={{t
                          "settings.activity_history.table.nothing_matches_filters"
                        }}
                      />
                    </div>
                  </:emptyState>
                </PrismTable>
              </div>
            </div>
          </:content>

          <:footer>
            {{#if @controller.totalRecords}}
              <GeneralTablesPaginationFooter
                @currentPage={{@controller.page}}
                @lastPage={{@controller.totalPages}}
                @onChangePageSize={{@controller.onChangePageSize}}
                @onSetPage={{@controller.setPage}}
                @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
                @pageSizeOptions={{@controller.pageSizeOptions}}
                @pageSizeValue={{@controller.currentPageSizeOption}}
                @showingText={{@controller.showingText}}
                @testIdPrefix="activity-history"
              />
            {{/if}}
          </:footer>
        </PrismCard>
      </div>
    </:default>
  </SettingsPageTemplate>

  {{#if @controller.isMappingChangesModalOpen}}
    <SettingsActivityModalMappingChanges
      @columns={{@controller.mappingChangesModalColumns}}
      @onClose={{@controller.closeMappingChangesModal}}
      @rows={{@controller.mappingChangesModalRows}}
    />
  {{/if}}
</template>;

export default HotelSettingsActivity;
